// src/firebase/registry-service.ts
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage';
import { db } from './firebase-init';

interface RegistryData {
  title: string;
  shortBio?: string;
  longBio?: string;
  backgroundPhotoURL?: string;
  profilePhotoURL?: string;
  customURL?: string;
  patientId: string;
  caregiverId?: string;
  userType: 'patient' | 'caregiver';
  cancerType?: string;
  status: 'public' | 'private' | 'draft';
  location?: string;
  items?: any[];
  isRecommended?: boolean;
  [key: string]: any;
}

/**
 * Ensure image URL is valid, converting relative paths to absolute if needed
 * @param imageUrl The image URL to check
 * @returns Valid image URL or empty string
 */
export const ensureValidImageUrl = (imageUrl: string | undefined | null): string => {
  if (!imageUrl) return '';

  // If it's already an absolute URL (from Firebase or external source), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // For relative paths within the app, convert to absolute
  if (imageUrl.startsWith('/')) {
    // In production, we'd need to handle this differently
    // For now, just make it clear this is a local asset
    return window.location.origin + imageUrl;
  }

  return imageUrl;
};

/**
 * Upload an image to Firebase Storage
 * @param file File to upload
 * @param path Path in storage (e.g., 'registry-photos/user-123')
 * @returns Download URL for the uploaded image
 */
export const uploadImage = async (file: File, path: string): Promise<string> => {
  try {
    const storage = getStorage();
    
    // Clean the filename to remove special characters
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    
    // Add timestamp to ensure uniqueness
    const timestamp = Date.now();
    const uniqueFileName = `${timestamp}_${cleanFileName}`;
    
    // Create storage reference
    const fileRef = storageRef(storage, `${path}/${uniqueFileName}`);
    
    console.log(`Uploading to path: ${path}/${uniqueFileName}`);

    try {
      // Detect if we're in development mode - if so, and if we're experiencing CORS issues,
      // just return a placeholder URL to allow development to continue
      const isDev = import.meta.env.MODE === 'development' || 
                   window.location.hostname === 'localhost' || 
                   window.location.hostname.includes('127.0.0.1');
      
      if (isDev) {
        try {
          // Try uploading first
          const snapshot = await uploadBytesResumable(fileRef, file);
          console.log('Upload successful');
          
          // Get the download URL
          const downloadURL = await getDownloadURL(snapshot.ref);
          console.log('Download URL:', downloadURL);
          return downloadURL;
        } catch (uploadError: any) {
          console.warn('Development mode: Using image file as base64 data URL due to CORS issues');
          
          // Create a FileReader to convert the file to a data URL
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              // Return the data URL which can be used directly in img src
              resolve(reader.result as string);
            };
            reader.readAsDataURL(file);
          });
        }
      } else {
        // In production, we try the normal upload
        const snapshot = await uploadBytesResumable(fileRef, file);
        return await getDownloadURL(snapshot.ref);
      }
    } catch (uploadError: any) {
      console.error('Upload failed:', uploadError);
      
      // Check for CORS errors
      if (uploadError.message && uploadError.message.includes('CORS')) {
        console.error('CORS error detected. Please check Firebase Storage CORS configuration.');
      }
      
      throw uploadError;
    }
  } catch (error) {
    console.error('Error in uploadImage function:', error);
    throw error;
  }
};

/**
 * Check if a custom URL is available
 * @param customURL The custom URL to check
 * @returns boolean indicating if the URL is available
 */
export const isCustomURLAvailable = async (customURL: string): Promise<boolean> => {
  try {
    const registriesRef = collection(db, 'registries');
    const q = query(registriesRef, where('customURL', '==', customURL));
    const querySnapshot = await getDocs(q);

    return querySnapshot.empty;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new registry
 * @param registryData Registry data including title, bios, photo URLs, etc.
 * @param userId User ID of the registry creator
 * @returns Created registry object with ID and URL
 */
export const createRegistry = async (registryData: RegistryData, userId: string): Promise<{id: string, url: string}> => {
  try {
    // Check if the user is allowed to create a registry (patient or caregiver)
    if (registryData.userType !== 'patient' && registryData.userType !== 'caregiver') {
      throw new Error('Only patients and caregivers can create registries');
    }

    // Generate a URL for the registry
    let url = '';

    // If custom URL is provided and available, use it
    if (registryData.customURL) {
      const customURLClean = registryData.customURL
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')  // Remove special chars except hyphens
        .replace(/\s+/g, '-');      // Replace spaces with hyphens

      const isAvailable = await isCustomURLAvailable(customURLClean);

      if (isAvailable) {
        url = customURLClean;
      } else {
        // If not available, append a timestamp
        const timestamp = Date.now();
        url = `${customURLClean}-${timestamp}`;
      }
    } else {
      // Generate a URL from the title
      const urlBase = registryData.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      const timestamp = Date.now();
      url = `${urlBase}-${timestamp}`;
    }

    // Prepare the registry data
    const registryDoc = {
      ...registryData,
      customURL: url,
      patientId: registryData.userType === 'patient' ? userId : registryData.patientId,
      caregiverId: registryData.userType === 'caregiver' ? userId : registryData.caregiverId,
      status: registryData.status || 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Ensure image URLs are valid
      profilePhotoURL: ensureValidImageUrl(registryData.profilePhotoURL),
      backgroundPhotoURL: ensureValidImageUrl(registryData.backgroundPhotoURL)
    };

    // Create registry document
    const registryRef = await addDoc(collection(db, 'registries'), registryDoc);

    return { id: registryRef.id, url };
  } catch (error) {
    throw error;
  }
};

/**
 * Get a registry by custom URL
 */
export const getRegistryByUrl = async (url: string) => {
  try {
    const registriesRef = collection(db, 'registries');
    const q = query(registriesRef, where('customURL', '==', url));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const registryDoc = querySnapshot.docs[0];
    return {
      id: registryDoc.id,
      ...registryDoc.data()
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get a registry by ID
 */
export const getRegistryById = async (registryId: string) => {
  try {
    const registryRef = doc(db, 'registries', registryId);
    const registrySnap = await getDoc(registryRef);

    if (!registrySnap.exists()) {
      return null;
    }

    return {
      id: registrySnap.id,
      ...registrySnap.data()
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Update a registry
 */
export const updateRegistry = async (registryId: string, updatedData: Partial<RegistryData>) => {
  try {
    console.log(`Starting update for registry ${registryId} with data:`, updatedData);
    
    if (!registryId) {
      throw new Error('Registry ID is required for updates');
    }
    
    // Validate that we're connected to Firestore
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }
    
    const registryRef = doc(db, 'registries', registryId);
    
    // Verify the document exists before updating
    const registrySnap = await getDoc(registryRef);
    if (!registrySnap.exists()) {
      throw new Error(`Registry with ID ${registryId} not found`);
    }
    
    console.log('Found registry document:', registrySnap.data());

    // Check if we're updating the customURL
    if (updatedData.customURL) {
      const customURLClean = updatedData.customURL
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      
      console.log(`Processing custom URL update from ${updatedData.customURL} to ${customURLClean}`);

      // Check if this URL is already in use by another registry
      const isAvailable = await isCustomURLAvailable(customURLClean);
      console.log(`Custom URL '${customURLClean}' available:`, isAvailable);

      if (!isAvailable) {
        // Get the current registry to see if it already has this URL
        const currentRegistry = await getRegistryById(registryId);

        // If it's not the current registry's URL, then it's in use by another registry
        if (currentRegistry && currentRegistry.customURL !== customURLClean) {
          throw new Error('This custom URL is already in use');
        }
      }

      // Update with the cleaned URL
      updatedData.customURL = customURLClean;
    }

    // Handle photo removals: convert null to empty string 
    // This is important for Firestore filtering and UI rendering
    if (updatedData.profilePhotoURL === null) {
      console.log('Removing profile photo - setting to empty string');
      updatedData.profilePhotoURL = '';
    }
    
    if (updatedData.backgroundPhotoURL === null) {
      console.log('Removing background photo - setting to empty string');
      updatedData.backgroundPhotoURL = '';
    }

    // Add updatedAt timestamp to the payload
    const updatePayload = {
      ...updatedData,
      updatedAt: serverTimestamp()
    };
    
    console.log('Sending update to Firestore with payload:', updatePayload);
    
    // Perform the update
    await updateDoc(registryRef, updatePayload);
    console.log('Update successful!');

    return { success: true, message: 'Registry updated successfully', id: registryId };
  } catch (error) {
    console.error('Error in updateRegistry:', error);
    throw error;
  }
};

/**
 * Delete a registry
 */
export const deleteRegistry = async (registryId: string) => {
  try {
    await deleteDoc(doc(db, 'registries', registryId));
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all public registries
 */
export const getPublicRegistries = async (limitCount = 20) => {
  try {
    const registriesRef = collection(db, 'registries');
    const q = query(
      registriesRef,
      where('status', '==', 'public'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

/**
 * Get all registries
 */
export const getAllRegistries = async () => {
  try {
    const registriesRef = collection(db, 'registries');
    const q = query(
      registriesRef,
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

/**
 * Get all recommended registries
 */
export const getRecommendedRegistries = async () => {
  try {
    const registriesRef = collection(db, 'registries');
    const q = query(
      registriesRef,
      where('isRecommended', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

/**
 * Get registries by user ID (either as patient or caregiver)
 */
export const getUserRegistries = async (userId: string) => {
  try {
    const registriesRef = collection(db, 'registries');

    // Query for registries where user is either patient or caregiver
    const patientQuery = query(
      registriesRef,
      where('patientId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const caregiverQuery = query(
      registriesRef,
      where('caregiverId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    // Execute both queries
    const [patientResults, caregiverResults] = await Promise.all([
      getDocs(patientQuery),
      getDocs(caregiverQuery)
    ]);

    // Combine and deduplicate results
    const registries = new Map();

    patientResults.docs.forEach(doc => {
      registries.set(doc.id, {
        id: doc.id,
        ...doc.data(),
        role: 'patient'
      });
    });

    caregiverResults.docs.forEach(doc => {
      if (!registries.has(doc.id)) {
        registries.set(doc.id, {
          id: doc.id,
          ...doc.data(),
          role: 'caregiver'
        });
      } else {
        // If already in the results as patient, update the role
        const existing = registries.get(doc.id);
        existing.role = 'both';
        registries.set(doc.id, existing);
      }
    });

    return Array.from(registries.values());
  } catch (error) {
    throw error;
  }
};

/**
 * Add a registry item
 */
export const addRegistryItem = async (registryId: string, itemData: any) => {
  try {
    const itemRef = await addDoc(collection(db, 'registry_items'), {
      ...itemData,
      registryId,
      purchased: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return {
      id: itemRef.id,
      ...itemData
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get all items for a registry
 */
export const getRegistryItems = async (registryId: string) => {
  try {
    const itemsRef = collection(db, 'registry_items');
    const q = query(
      itemsRef,
      where('registryId', '==', registryId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

/**
 * Update a registry item
 */
export const updateRegistryItem = async (itemId: string, updatedData: any) => {
  try {
    const itemRef = doc(db, 'registry_items', itemId);

    await updateDoc(itemRef, {
      ...updatedData,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a registry item
 */
export const deleteRegistryItem = async (itemId: string) => {
  try {
    await deleteDoc(doc(db, 'registry_items', itemId));
    return true;
  } catch (error) {
    throw error;
  }
};

export default {
  uploadImage,
  isCustomURLAvailable,
  createRegistry,
  getRegistryByUrl,
  getRegistryById,
  updateRegistry,
  deleteRegistry,
  getPublicRegistries,
  getUserRegistries,
  addRegistryItem,
  getRegistryItems,
  updateRegistryItem,
  deleteRegistryItem,
  ensureValidImageUrl
};
